import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Users,
  Video,
  GraduationCap,
  Check,
  Quote,
  Sparkles,
} from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { fileUrl } from "../uploads";
import { STAGES, TESTIMONIALS, MEMBERSHIP_BENEFITS, BRAND } from "../mock";

const FEATURES = [
  { icon: BookOpen, title: "Artículos", text: "Contenido gratuito y de calidad para cada etapa.", to: "/articulos" },
  { icon: GraduationCap, title: "Cursos", text: "Formación online paso a paso, a tu ritmo.", to: "/cursos" },
  { icon: Users, title: "Comunidad", text: "Foros privados para compartir y sentirte acompañada.", to: "/comunidad" },
  { icon: Video, title: "Sesiones", text: "Encuentros grupales mensuales en directo por Zoom.", to: "/sesiones" },
];

export default function Home() {
  const { articles } = useStore();
  const { isAuthenticated, user } = useAuth();
  const latest = articles.slice(0, 3);

  return (
    <div className="animate-fade-in">
      {/* HERO (solo visible si NO ha iniciado sesión) */}
      {!isAuthenticated && (
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber/30 to-cream" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-terracotta bg-terracotta/10 px-4 py-2 rounded-full">
              <Sparkles size={14} /> Embarazo · Puerperio · Menopausia
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-plum leading-[1.05] mt-6">
              Un espacio para tus etapas transformadoras
            </h1>
            <p className="font-body text-lg text-ink/70 mt-6 max-w-xl leading-relaxed">
              Cada etapa transformadora de tu vida merece un espacio de escucha,
              cuidado y conocimiento. Estoy aquí para recorrer este camino contigo.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link
                to="/membresia"
                className="inline-flex items-center gap-2 bg-plum text-cream font-sans px-7 py-3.5 rounded-full hover:bg-[#684d89] transition-colors shadow-md"
              >
                Únete a la comunidad <ArrowRight size={18} />
              </Link>
              <Link
                to="/articulos"
                className="inline-flex items-center gap-2 border border-plum/30 text-plum font-sans px-7 py-3.5 rounded-full hover:bg-plum/5 transition-colors"
              >
                Leer artículos gratis
              </Link>
            </div>
            <p className="font-body text-sm text-ink/50 mt-5">
              Artículos gratuitos sin registro · Membresía completa desde{" "}
              <span className="text-terracotta font-semibold">
                {BRAND.membershipPrice}
                {BRAND.currency}/mes
              </span>
            </p>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-rose/40 blur-2xl" />
            <div className="absolute -bottom-8 -right-6 w-40 h-40 rounded-full bg-terracotta/30 blur-2xl" />
            <img
              src="/images/etapas-grupo.png"
              alt="Mujeres en distintas etapas"
              className="relative rounded-[2rem] shadow-2xl w-full object-cover"
            />
          </div>
        </div>
      </section>
      )}

      {/* WELCOME (solo versión registrada) */}
      {isAuthenticated && (
        <section className="relative overflow-hidden bg-gradient-to-b from-amber/30 to-cream">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-20 text-center animate-fade-up">
            <span className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-terracotta bg-terracotta/10 px-4 py-2 rounded-full">
              <Sparkles size={14} /> Comunidad matrum
            </span>
            <h1 className="font-serif text-5xl md:text-6xl text-plum mt-6 leading-tight">
              Bienvenida a <span className="font-serif italic text-terracotta text-6xl md:text-7xl">matrum</span>{user?.name?.split(" ")[0] ? `, ${user.name.split(" ")[0]}` : ""}
            </h1>
            <p className="font-body text-lg text-ink/70 mt-5 max-w-2xl mx-auto leading-relaxed">
              Nos alegra tenerte aquí. Este es tu espacio de escucha, cuidado y
              acompañamiento. Explora los contenidos y camina con nosotras.
            </p>
            <div className="mt-10 max-w-md mx-auto">
              <img
                src="/images/comunidad-madres.png"
                alt="Mujeres acompañándose en el embarazo, el puerperio y la maternidad"
                className="w-full rounded-[1.5rem] shadow-lg object-cover"
                data-testid="welcome-community-image"
              />
            </div>
          </div>
        </section>
      )}

      {/* STAGES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-serif text-4xl md:text-5xl text-plum">
            Tres etapas, un mismo acompañamiento
          </h2>
          <p className="font-body text-ink/65 mt-4 text-lg">
            Contenido y cuidado adaptado al momento que estás viviendo.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-7">
          {STAGES.map((s) => (
            <div
              key={s.id}
              className="group relative rounded-3xl overflow-hidden shadow-lg cursor-default"
            >
              <img
                src={s.image}
                alt={s.title}
                className="h-[420px] w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7 text-cream">
                <span className="font-sans text-xs uppercase tracking-[0.18em] text-amber">
                  {s.subtitle}
                </span>
                <h3 className="font-serif text-3xl mt-1">{s.title}</h3>
                <p className="font-body text-cream/85 text-sm mt-2 leading-relaxed">
                  {s.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT LORENA */}
      <section className="bg-white/60">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img
              src="/images/lorena-perfil.png"
              alt="Lorena Cotano"
              className="rounded-[2rem] shadow-xl w-full object-cover max-h-[520px]"
            />
          </div>
          <div>
            <span className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta">
              Sobre mí
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-plum mt-3 leading-tight">
              Soy Lorena, matrona y sexóloga
            </h2>
            <p className="font-body text-ink/70 mt-6 text-lg leading-relaxed">
              Acompaño a las mujeres desde la honestidad y la ternura en los
              momentos más transformadores de su vida. Creo en una maternidad y
              una feminidad cuidadas desde dentro, con información, respeto y
              comunidad.
            </p>
            <blockquote className="mt-7 border-l-4 border-terracotta pl-5">
              <p className="font-serif italic text-2xl text-plum">
                “Ninguna mujer debería transitar sola sus etapas. Juntas nos
                sostenemos, nos escuchamos y nos transformamos.”
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="font-serif text-4xl md:text-5xl text-plum">
            Todo lo que encontrarás
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((f) => (
            <Link
              key={f.title}
              to={f.to}
              className="group bg-white rounded-2xl p-7 shadow-sm border border-amber/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-2xl bg-amber/40 flex items-center justify-center text-plum group-hover:bg-plum group-hover:text-cream transition-colors">
                <f.icon size={26} />
              </div>
              <h3 className="font-serif text-2xl text-plum mt-5">{f.title}</h3>
              <p className="font-body text-ink/65 mt-2 text-sm leading-relaxed">
                {f.text}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* LATEST ARTICLES */}
      <section className="bg-white/60">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <span className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta">
                Acceso gratuito
              </span>
              <h2 className="font-serif text-4xl md:text-5xl text-plum mt-2">
                Últimos artículos
              </h2>
            </div>
            <Link
              to="/articulos"
              className="inline-flex items-center gap-2 font-sans text-plum hover:text-terracotta transition-colors"
            >
              Ver todos <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-7">
            {latest.map((a) => (
              <Link
                key={a.id}
                to={`/articulos/${a.id}`}
                className="group bg-cream rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src={fileUrl(a.cover)}
                    alt={a.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <span className="font-sans text-[11px] uppercase tracking-[0.15em] text-terracotta">
                    {a.stage}
                  </span>
                  <h3 className="font-serif text-2xl text-plum mt-2 leading-tight group-hover:text-terracotta transition-colors">
                    {a.title}
                  </h3>
                  <p className="font-body text-ink/65 text-sm mt-3 leading-relaxed line-clamp-2">
                    {a.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MEMBERSHIP CTA (solo visible si NO ha iniciado sesión) */}
      {!isAuthenticated && (
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="relative rounded-[2.5rem] overflow-hidden bg-plum text-cream px-8 py-14 lg:px-16 lg:py-20">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-terracotta/30 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight">
                Forma parte de la comunidad
              </h2>
              <p className="font-body text-cream/80 mt-5 text-lg leading-relaxed">
                Accede a todos los cursos, foros privados y sesiones grupales
                online por solo{" "}
                <span className="text-amber font-semibold">
                  {BRAND.membershipPrice}
                  {BRAND.currency}/mes
                </span>
                . Cancela cuando quieras.
              </p>
              <Link
                to="/membresia"
                className="inline-flex items-center gap-2 mt-8 bg-amber text-plum font-sans font-semibold px-8 py-4 rounded-full hover:bg-[#e0b88a] transition-colors"
              >
                Quiero unirme <ArrowRight size={18} />
              </Link>
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
              {MEMBERSHIP_BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 font-body text-cream/90">
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-amber/30 flex items-center justify-center shrink-0">
                    <Check size={14} className="text-amber" />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      )}

      {/* TESTIMONIALS */}
      <section className="bg-white/60">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <h2 className="font-serif text-4xl md:text-5xl text-plum text-center mb-14">
            Lo que dicen las mujeres de la comunidad
          </h2>
          <div className="grid md:grid-cols-3 gap-7">
            {TESTIMONIALS.map((t) => (
              <div
                key={t.id}
                className="bg-cream rounded-2xl p-8 shadow-sm border border-amber/30"
              >
                <Quote size={32} className="text-terracotta/50" />
                <p className="font-body text-ink/75 mt-4 leading-relaxed italic">
                  “{t.text}”
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-plum text-cream flex items-center justify-center font-serif text-lg">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-sans font-semibold text-plum">{t.name}</div>
                    <div className="font-body text-xs text-terracotta">{t.stage}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
